/* @flow */
/* eslint-env es6,browser */

import * as u from './utils';
import adconfig from './adconfig';
import adrotation from './adrotation';
import { BrowserUtil } from './utils/browserutil';
import { _timedScheduler, _RAFScheduler } from './utils/scheduler';
import { adcontainer } from './adcontainer';
import { cachedAdcontainer } from './cachedcontainer';

/**
 * @description Implements functionality for ad loading
 * @exports adloader
 * @requires  module:utils
 * @requires  module:adconfig
 * @requires  module:adrotation
 * @requires  module:adscheduler
 * @requires  module:browserutil
 * @requires  module:adcontainer
 * @requires  module:cachedAdContainer
 */
class _adloader {
  _doc: Document;
  _win: window;
  _adInfo: adPosInfoWrapper;
  _adPage: string;
  _RAFScheduler: Scheduler;
  _timedScheduler: Scheduler;
  _scheduler: Scheduler;
  _defConfig: adloadingConfig;
  _config: adloadingConfig;
  constructor() {
    this._adInfo = {};
    this._adPage = '';
    this._doc = document;
    this._win = window;
    this._defConfig = {
      delay: 200,
      enableRAFScheduler: false,
      sandbox: ''
    };
    this._config = u.extend(true, {}, this._defConfig);
    this._RAFScheduler = new _RAFScheduler();
    this._timedScheduler = new _timedScheduler();
    [this._RAFScheduler, this._timedScheduler].forEach(
      // set handlers in scheduler for processing, priortizing & end of processing
      s =>
        s.setProperties(
          this._config.delay,
          obj => this.loadIframeAd(obj),
          list => this._prioritizeAboveFold(list),
          () => adrotation.trigger()
        )
    );
    this._scheduler = this._getScheduler();
  }

  /**
   * Initialize the object
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: window.document, g: globalParam) {
    this._adPage = g.adPage;
    this._doc = d;
    this._win = w;

    //preload ad page
    u.preloadResource(this._doc, this._adPage);
    [adcontainer, cachedAdcontainer].forEach(c => c.init(g));
  }

  /**
   * setter for adloader configs
   * t - delay between loading 2 ad positions
   * enableRAFScheduler - whether to enable RAF scheduler
   * @param {Object} o - Config
   * @returns {undefined}
   */
  updateConfig(o: Object) {
    this._config = u.extend(true, {}, this._defConfig, o);
    //this._scheduler.clear();
    this._scheduler = this._getScheduler();
  }

  /**
   * prioritize above fold positions
   * @param {Array<adInfo>} list - Position list
   * @return {number} - First above the fold position index
   * @private
   */
  _prioritizeAboveFold(list: Array<adInfo>): number {
    return list.findIndex(o => adconfig.getPosSettings(o.id).aFold);
  }

  /**
   * get ad scheduler
   * @returns {Object} - Scheduler object
   * @private
   */
  _getScheduler() {
    return this._config.enableRAFScheduler && window.requestAnimationFrame
      ? this._RAFScheduler
      : this._timedScheduler;
  }

  /**
   * load ad content using iframeDoc Write
   * @param {adInfo} adInfoObj - AdInfo object
   * @returns {undefined}
   * @private
   */
  _loadAdContentInOldIE(adInfoObj: adInfo) {
    // caching is disabled in this loading method, using adcontainer by default
    let newIF = adcontainer.loadIframeAd('javascript:false', adInfoObj);
    if (!newIF) {
      return;
    }

    //let us write ad content
    let ifrDoc = u.getIfrDoc(newIF);
    try {
      if (ifrDoc) {
        ifrDoc.open();
        ifrDoc.write(
          `<script>window.top.__as.populateCustomVar(window, document, '${
            adInfoObj.id
          }');</script>${adInfoObj.content}`
        );
        //not doing ifrDoc close for old IE browsers
        if (adInfoObj.as.loadingMethod === 2 && !BrowserUtil.isOldIE()) {
          ifrDoc.close();
        }
      }
    } catch (e) {
      u.logError(
        `loadAdContent ${adInfoObj.id} iframe doc write failed: ${e.message}`
      );
    }
  }

  /**
   * load iframe Ad
   * @param {adInfo} adInfoObj - AdInfo object
   * @returns {HTMLIFrameElement} - Newly created iframe DOM element
   */
  loadIframeAd(adInfoObj: adInfo) {
    // cache obj in adInfo
    this._adInfo[adInfoObj.id] = u.extend(true, {}, adInfoObj);

    // load ads without blankPhnx.html only for old IE browsers
    if (adInfoObj.as.loadingMethod === 2 || BrowserUtil.isOldIE()) {
      return this._loadAdContentInOldIE(adInfoObj);
    }

    // For all other browsers load ads using blankPhnx.html
    let cont = cachedAdcontainer.isCachingEnabled(
      adInfoObj.id,
      adInfoObj.as.iframe
    )
      ? cachedAdcontainer
      : adcontainer;
    return cont.loadIframeAd(this._adPage, adInfoObj);
  }

  /**
   * loading global script/css ad requests
   * @param {string} data - Ad data
   * @returns {undefined}
   * @private
   */
  _loadGlobalScriptCssAd(data: string) {
    let info: { js: Array<string>, css: Array<string> } = u.extend(
      true,
      {},
      { js: [], css: [] },
      u.parseJSON(this._win, data)
    );
    info.js.forEach(js => u.loadJS(js, this._doc));
    info.css.forEach(css => u.loadCSS(css, this._doc));
  }

  /**
   * Populate custom var for cases where AD is load using iframeDoc.write
   * @param {window} win - window object
   * @param {window.document} doc - document object
   * @param {string} id - ad position
   * @returns {undefined}
   */
  populateCustomVar(win: window, doc: Document, id: string) {
    let info = this._adInfo[id];
    if (info === null || typeof info === 'undefined') {
      return;
    }
    //set global variables
    Object.keys(info.customVars).forEach(i => {
      win[i] = info.customVars[i];
    });
  }

  /**
   * @global
   * @typedef {Object} adInfo
   * @property {string} id - ad position
   * @property {string} content - ad content
   * @property {Object} [customVars] - custom variables to be populated
   * @property {boolean} adRotation - whether ad rotation is enabled for this position
   * @property {string} fID - ad iframe id
   * @property {string} cID - ad div container id
   * @property {posConfigInfo} as - ad setting for position
   */
  /**
   * get ad info object - used for cases where ads are loaded using blankPhnx.html
   * @param {string} pos - Position name
   * @return {adInfo} - AdInfo object
   */
  getAdInfo(pos: string) {
    return this._adInfo[pos];
  }

  /**
   * clear ad loading list
   * @returns {undefined}
   */
  clearList() {
    this._scheduler.clear();
  }

  /**
   * @global
   * @type function
   * @callback contentCB - Callback fn
   * @param {string} page - pagename
   * @param {string} pos - ad position
   */
  /**
   * load ad
   * @param {string} pos - Ad Position
   * @param {string|contentCB} content - Ad content
   * @param {Object} [cVar] - Custom var object
   * @returns {undefined}
   */
  load(
    pos: string,
    content: string | ((string, string) => string),
    cVar: Object
  ) {
    pos = pos || '';
    content = content || '';
    if (!pos.length) {
      return false;
    }

    let as = adconfig.getPosSettings(pos);
    if (as.iframe !== undefined && !as.iframe && u.isString(content)) {
      // loading ads directly in the ad div without using iframe
      // no caching for non-iframe ads, loading using adcontainer directly
      // $FlowFixMe
      return adcontainer.loadNonIframeAd(pos, content);
    }

    if (as.gscript !== undefined && as.gscript && u.isString(content)) {
      // loading global scripts/styles
      // $FlowFixMe
      return this._loadGlobalScriptCssAd(content);
    }

    let callback =
      as.callback && as.callback.length ? this._win[as.callback] : undefined;
    if (callback && callback !== undefined && u.isFunction(callback)) {
      //Trim data for callback function
      content = content.replace(/^\s+|\s+$|(\r\n)/g, '');
      let tmp2 = callback.call(this._win, adconfig.getPageName(), pos, content);
      let tmp: { bHandled: boolean, data: string } = u.extend(
        true,
        { bHandled: false, data: content },
        tmp2
      );
      if (tmp.bHandled) {
        return true;
      }

      //data might have been modified by the callback
      content = tmp.data;
    }

    if (u.isFunction(content)) {
      // $FlowFixMe
      content = content.call(this._win, adconfig.getPageName(), pos);
    }

    if (!u.isObject(cVar)) {
      cVar = {};
    }

    //add adInfo object to scheduler queue
    return this._scheduler.enqueue({
      id: pos,
      content: content,
      customVars: u.extend(true, {}, cVar, {
        UOL_POS: pos,
        UOL_PAGENAME: adconfig.getPageName(),
        UOL_ADROTATE: adrotation.getRotationStatus()
      }),
      adRotation: cachedAdcontainer.isCachingEnabled(pos, as.iframe),
      sandbox: this._config.sandbox,
      as: as,
      cID: '',
      fID: ''
    });
  }

  /**
   * toggle Ad for cached container
   * @param {string} adPos - ad position
   * @returns {undefined}
   */
  toggleAD(adPos: string) {
    cachedAdcontainer.toggleAd(adPos);
  }
}

//export singleton object as default for this module
export default new _adloader();
